import {
  applyContentPreservingSelection,
  createSafeEditorLogger,
  renderStreamingMarkdownToHtml,
  toSafeEditorErrorSummary,
  type EditorLogger,
  type SafeEditorErrorSummary,
} from '../core';
// Type-only: loads @tiptap/markdown's Editor augmentation (editor.getMarkdown).
import { type Editor } from '@tiptap/core';
import type {} from '@tiptap/markdown';
import { useEffect, useRef } from 'react';
import { runControlledApply } from './internal/content-apply-marks';

/** Content the host hydrates from: canonical markdown, or legacy HTML. */
export type ControlledContentType = 'markdown' | 'html';

/**
 * A content apply that failed, reported to the host so it can log and emit its
 * own product telemetry. `error` is the typed content-free summary — never the
 * raw throw, which can echo document content (task 4.10). `stage` separates an
 * ordinary hydration failure from a failed post-stream canonical re-apply,
 * which leaves the document in its tolerant preview-HTML form.
 */
export interface ContentApplyFailure {
  error: SafeEditorErrorSummary;
  contentType: ControlledContentType;
  contentLength: number;
  stage: 'render' | 'finalize';
}

/**
 * A content apply that succeeded but could not restore the prior selection.
 * Separate from `ContentApplyFailure` because the document is intact and no
 * product telemetry is owed — this is an observability signal only, and it
 * must not be conflated with a failed apply's error codes.
 */
export type SelectionRestoreFailure = SafeEditorErrorSummary;

const getCurrentContent = (editor: Editor, contentType: ControlledContentType) =>
  contentType === 'markdown' ? editor.getMarkdown() : editor.getHTML();

// Editors whose document currently holds the tolerant streaming-preview HTML
// rather than canonically parsed markdown. The taint is undetectable by the
// markdown compare (preview HTML can serialize to the identical markdown), so
// it must be tracked. Keyed on the editor — not a hook ref — so the owed
// canonical re-apply survives a hook unmount/remount and reconciles on any
// later non-streaming sync, not only on the exact stream-end render.
// Deliberately not editor.storage: this hook owns no extension, and module
// privacy keeps the taint writable only by the sync paths that manage it.
const previewDirtyEditors = new WeakSet<Editor>();

// The pure streaming render transform lives in @heidi/editor/core (task 5.6).
// Imported (the hook calls it below) and re-exported so existing importers
// keep their path.

/**
 * Syncs editor content when the content prop changes, comparing in the
 * hydration format to prevent feedback loops: after a save the prop is exactly
 * what `getMarkdown()` emitted, so the editor is never reset mid-typing.
 *
 * `contentType` is `'markdown'` for canonical notes; `'html'` is the
 * lazy-migration read path for legacy notes (TipTap's own HTML parser — the
 * first save flattens them to markdown).
 *
 * The markdown-format comparison makes a paused edit a fixed point, so a save
 * round-trip never resets the editor. To also cover the race where a save lands
 * while the user has resumed typing, the non-streaming apply is skipped while
 * the editor is focused and editable — only the user's own echo is suppressed;
 * generated (non-editable) and streamed content still apply.
 *
 * `forceApply` overrides that focus guard for a single content change: a chat
 * "Add to note" / "Replace note" insert is a genuine external write that the
 * guard can't distinguish from an echo, so it must apply even while focused.
 * The streaming throttle still wins so a forced flag can't thrash mid-stream.
 *
 * Streaming renders through a tolerant path so content appears progressively
 * (matching the vMarkdown editor): the canonical markdown `setContent` throws on
 * the partial/intermediate states a stream produces, which would freeze the
 * editor until the complete note lands. While `isStreaming`, each throttled
 * chunk is rendered as HTML via `marked` (which never throws on partial
 * markdown) through ProseMirror's tolerant DOMParser. An editor hydrated that
 * way is marked preview-dirty; every non-streaming sync re-applies the content
 * through the canonical markdown path until one succeeds, so the final document
 * is byte-identical to the markdown editor — no refetch. The reconcile is
 * idempotent and re-entrant: a failed finalize stays dirty and retries on the
 * next sync, and the taint survives a hook remount against the same editor.
 */
export const useControlledContentSync = ({
  editor,
  content,
  contentType = 'markdown',
  isStreaming = false,
  forceApply = false,
  onApplyFailure,
  onSelectionRestoreFailure,
  onLog,
}: {
  editor: Editor | null | undefined;
  content: string;
  contentType?: ControlledContentType;
  isStreaming?: boolean;
  forceApply?: boolean;
  onApplyFailure?: (failure: ContentApplyFailure) => void;
  onSelectionRestoreFailure?: (error: SelectionRestoreFailure) => void;
  /**
   * The host's logger adapter (task 4.10). Optional and no-op by default, so
   * supplying nothing leaves every existing consumer's trace unchanged. Only
   * narrowed `EditorLogRecord`s cross it — never a raw error, never content.
   */
  onLog?: EditorLogger;
}) => {
  const pendingContentRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const onLogRef = useRef(onLog);
  onLogRef.current = onLog;

  useEffect(() => {
    // Rebuilt per effect run over the ref, so a host passing a fresh closure
    // every render neither re-runs the effect nor leaves a stale sink behind.
    const log = createSafeEditorLogger((record) => onLogRef.current?.(record));

    const applyContent = (next: string, streaming: boolean) => {
      // Only invoked under the non-null guard below; narrow for the closures.
      if (!editor) return;
      const isFinalize = !streaming && previewDirtyEditors.has(editor);
      // Mark the synchronous apply so the controller's origin classifier never
      // emits a curated content callback for a package-performed apply (D5).
      // Matters for the one apply that DOES fire `update` — the characterized
      // empty-clear, whose frozen single-autosave trace (ARCFE-863) must keep
      // reaching the host `update` listener while the curated callback stays
      // silent. Zero observable change on every other path (emitUpdate: false).
      runControlledApply(editor, () =>
        applyContentPreservingSelection(
          editor,
          () => {
            if (next.trim().length === 0) {
              // @tiptap/markdown's setContent throws "Invalid content for node
              // doc" on empty/whitespace markdown; clear the doc instead.
              editor.commands.clearContent();
              previewDirtyEditors.delete(editor);
              return;
            }
            if (streaming && contentType === 'markdown') {
              // Render the partial stream as HTML: marked tolerates incomplete
              // markdown and DOMParser coerces the result, where the canonical
              // markdown setContent would throw and freeze the editor.
              editor.commands.setContent(renderStreamingMarkdownToHtml(next), {
                contentType: 'html',
                emitUpdate: false,
                errorOnInvalidContent: false,
                parseOptions: { preserveWhitespace: 'full' },
              });
              previewDirtyEditors.add(editor);
              return;
            }
            editor.commands.setContent(next, {
              contentType,
              emitUpdate: false,
              errorOnInvalidContent: false,
              parseOptions: { preserveWhitespace: 'full' },
            });
            // Cleared only after a successful canonical parse: a throw skips
            // this line, so a failed finalize stays dirty and retries.
            previewDirtyEditors.delete(editor);
          },
          (error) => {
            // The package never logs and never hands out a raw editor error —
            // a setContent failure can echo the document in its message (task
            // 4.10). The host receives the typed content-free summary plus the
            // stage it failed at, and owns the analytics event; the logger
            // adapter carries the developer log line through the same envelope.
            const summary = toSafeEditorErrorSummary(error);
            onApplyFailure?.({
              error: summary,
              contentType,
              contentLength: next.length,
              stage: isFinalize ? 'finalize' : 'render',
            });
            log({
              level: 'error',
              code: 'content_apply_failed',
              error: summary,
              measures: { contentLength: next.length },
            });
          },
          (error) => {
            onSelectionRestoreFailure?.(error);
            log({ level: 'error', code: 'selection_restore_failed', error });
          }
        )
      );
    };

    if (editor && !editor.isDestroyed) {
      // Force the canonical re-apply while the doc holds preview HTML: the
      // HTML-rendered stream can serialize to the same markdown as the prop,
      // so the compare below would skip finalize and leave the doc in its
      // streamed (HTML) form.
      const needsFinalize = !isStreaming && previewDirtyEditors.has(editor);
      // Empty markdown can't round-trip through getMarkdown() (an empty doc
      // serializes to "&nbsp;"), so gate the blank case on isEmpty to avoid
      // re-applying every frame — but still clear a lingering preview taint
      // (a preview render can coerce to an empty doc). While streaming the doc
      // is in HTML form, so the markdown compare is unreliable — apply every
      // (throttled) chunk.
      const needsUpdate =
        content.trim().length === 0
          ? !editor.isEmpty || previewDirtyEditors.has(editor)
          : isStreaming ||
            needsFinalize ||
            getCurrentContent(editor, contentType) !== content;
      if (needsUpdate) {
        if (isStreaming) {
          pendingContentRef.current = content;
          if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
              rafIdRef.current = null;
              if (pendingContentRef.current !== null && !editor.isDestroyed) {
                applyContent(pendingContentRef.current, true);
                pendingContentRef.current = null;
              }
            });
          }
        } else if (
          forceApply ||
          needsFinalize ||
          !(editor.isFocused && editor.isEditable)
        ) {
          // Skip the echo while the user is actively editing (focused +
          // editable): a save landing one keystroke stale would reset the cursor
          // mid-type. Generated content (non-editable) and streamed updates (the
          // isStreaming path) still apply, as does a forced external insert (chat
          // Add to note / Replace note). The finalize of a preview-dirty doc
          // also bypasses the focus guard — it's the authoritative canonical
          // re-apply, not a user echo — and stays armed until an apply succeeds.
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
          }
          pendingContentRef.current = null;
          applyContent(content, false);
        }
      }
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      pendingContentRef.current = null;
    };
  }, [
    content,
    contentType,
    editor,
    isStreaming,
    forceApply,
    onApplyFailure,
    onSelectionRestoreFailure,
  ]);
};
